<script setup lang="ts">
import {computed, ref} from 'vue'
import {arrow, autoUpdate, flip, offset, Placement, shift, useFloating} from '@floating-ui/vue'

interface Props {
  reference: HTMLElement | null
  popperId: string
  placement?: Placement
  distance?: number
  popperClass?: string
  richContent?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  placement: 'top',
  distance: 5,
  popperClass: undefined,
  richContent: false,
})

const popperRef = ref<HTMLElement | null>(null)
const arrowRef = ref<HTMLElement | null>(null)

const {floatingStyles, placement: resolvedPlacement, middlewareData, isPositioned} = useFloating(
  computed(() => props.reference),
  popperRef,
  {
    placement: computed(() => props.placement),
    middleware: computed(() => [
      offset(props.distance),
      flip(),
      shift(),
      arrow({element: arrowRef}),
    ]),
    whileElementsMounted: autoUpdate,
  },
)

const popperStyles = computed(() => {
  return isPositioned.value ? floatingStyles.value : {
    ...floatingStyles.value,
    visibility: 'hidden' as const,
  }
})

const arrowStyles = computed(() => {
  const arrowData = middlewareData.value.arrow
  return {
    left: arrowData?.x != null ? `${arrowData.x}px` : undefined,
    top: arrowData?.y != null ? `${arrowData.y}px` : undefined,
  }
})

const isArrowOverflow = computed(() => Boolean(middlewareData.value.arrow?.centerOffset))
</script>

<template>
  <Teleport to="#tooltip-divs">
    <Transition
      appear
      enterFromClass="v-popper__popper--hidden v-popper__popper--show-from"
      enterActiveClass="v-popper__popper--show-to"
      enterToClass="v-popper__popper--shown"
      :duration="{enter: 100, leave: 0}"
    >
      <div
        :id="props.popperId"
        ref="popperRef"
        class="v-popper__popper v-popper--theme-tooltip"
        :class="[props.popperClass, {'v-popper__popper--arrow-overflow': isArrowOverflow}]"
        :data-popper-placement="resolvedPlacement"
        :style="popperStyles"
        role="tooltip"
      >
        <div class="v-popper__wrapper">
          <div class="v-popper__inner">
            <div>
              <div class="tooltip-text-wrapper" :class="{'tooltip-text-wrapper-rich': props.richContent}">
                <slot/>
              </div>
            </div>
          </div>
          <div ref="arrowRef" class="v-popper__arrow-container" :style="arrowStyles">
            <div class="v-popper__arrow-outer"/>
            <div class="v-popper__arrow-inner"/>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.tooltip-text-wrapper {
  display: -webkit-box;
  -webkit-line-clamp: 20;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;
  max-height: 500px;
  word-break: break-word;
  white-space: pre-wrap;
}

.tooltip-text-wrapper-rich {
  display: block;
  -webkit-line-clamp: unset;
  max-width: none;
  max-height: none;
  overflow: visible;
  white-space: normal;
  word-break: normal;
}
</style>
